import { useEffect, useRef, useState } from "react"
import type { CSSProperties } from "react"
import styles from "@shared/styles/components/General.module.css"
import designSystemStyles from "@shared/styles/designSystemStyles"

interface ImageWithPlaceholderProps {
    url?: string | null
    fallbackUrl?: string
    placeholderColor?: string
    width?: CSSProperties["width"]
    height?: CSSProperties["height"]
    objectFit?: CSSProperties["objectFit"]
    borderRadius?: CSSProperties["borderRadius"]
    alt?: string
    className?: string
}

export const ImageWithPlaceholder: React.FC<ImageWithPlaceholderProps> = ({
    url,
    fallbackUrl,
    placeholderColor = designSystemStyles.colorBackgroundGray3,
    width = "100%",
    height = "100%",
    objectFit = "cover",
    borderRadius = designSystemStyles.cornerRadiusComponent,
    alt = "",
    className
}) => {
    const [resolvedSrc, setResolvedSrc] = useState<string | null>(url ?? null)
    const [isLoading, setIsLoading] = useState(Boolean(url))
    const [hasError, setHasError] = useState(false)
    const imageRef = useRef<HTMLImageElement | null>(null)

    useEffect(() => {
        setResolvedSrc(url ?? null)
        setIsLoading(Boolean(url))
        setHasError(false)
    }, [url])

    useEffect(() => {
        if (!resolvedSrc) return
        const image = imageRef.current
        if (image?.complete && image.naturalWidth > 0) {
            setIsLoading(false)
        }
    }, [resolvedSrc])

    const handleLoad = () => {
        setIsLoading(false)
    }

    const handleError = () => {
        if (fallbackUrl && resolvedSrc !== fallbackUrl) {
            setResolvedSrc(fallbackUrl)
            setIsLoading(true)
            return
        }
        setIsLoading(false)
        setHasError(true)
    }

    return (
        <div
            className={[styles["image-wrapper"], className].filter(Boolean).join(" ")}
            style={{
                width,
                height,
                borderRadius,
                backgroundColor: placeholderColor
            }}
        >
            {resolvedSrc && !hasError && (
                <img
                    ref={imageRef}
                    src={resolvedSrc}
                    alt={alt}
                    onLoad={handleLoad}
                    onError={handleError}
                    style={{
                        width: "100%",
                        height: "100%",
                        objectFit,
                        display: "block"
                    }}
                />
            )}
            {isLoading && <span className={styles["image-skeleton"]} />}
        </div>
    )
}
